import React, { useContext, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from '@material-ui/core/styles';
import { generatePath, useHistory } from 'react-router-dom';
import { ClientContractCreateInterface, ClientContractCreation, Event, QueryParams } from 'types';
import { useBuyers } from 'hooks';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import clientRest from 'services/client';
import PageContent from 'components/Generic/PageContent';
import { notify } from 'components/Notifier';
import { MaterialPicker } from 'components/MaterialPicker';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import { Routes } from 'routes';
import { AppContext } from 'context';
import { ClientContractsFormSchema } from './ClientContractsForm.yup';
import RequiredFieldsLegend from './RequiredFieldsLegend';
import { TAB_PANEL_PARAM } from '../../pages/Referentiels/Clients/ClientShow';

const useStyles = makeStyles({
  form: {
    width: '510px',
    margin: 'auto',
  },
  fieldLine: {
    marginTop: '40px',
  },
  sharedLine: {
    display: 'flex',
    justifyContent: 'space-between',
    '& > div:first-child': {
      width: '356px',
    },
    '& > div:last-child': {
      width: '510px',
    },
  },
  materialLine: {
    width: '100%',
    '& > div': {
      margin: '0',
      '& > span': {
        marginLeft: '0px',
      },
      '& > p': {
        textAlign: 'justify',
        marginLeft: '14px',
      },
    },
  },
});

export interface ClientContractsFormProps {
  onCancel?: () => void | undefined;
  setEditing?: (isEditing: boolean) => void | undefined;
  client: string; // @id (for loop exception)
}

const ClientContractsForm: React.FC<ClientContractsFormProps> = ({ onCancel, setEditing, client }) => {
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'clientContracts' });
  const classes = useStyles();
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const history = useHistory();

  const backRoute = generatePath(Routes.ClientShow, { clientId: client }) + '?' + TAB_PANEL_PARAM + '=buyers';

  const params: QueryParams = { withoutClientContract: client };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buyers] = useBuyers(params);

  const schema = useMemo(() => ClientContractsFormSchema(t), [t]);
  const { control, handleSubmit, reset, setValue, watch } = useForm<ClientContractCreateInterface>({
    mode: 'onBlur',
    defaultValues: {
      buyer: '',
      materials: [],
    },
    resolver: yupResolver(schema),
  });
  const buyer = watch('buyer');
  const materialsAllowed = useMemo(
    () => buyers?.find(oBuyer => buyer && oBuyer['@id'] === buyer)?.acceptedMaterials?.map(material => material['@id']),
    [buyer, buyers],
  );

  const onSubmit: SubmitHandler<ClientContractCreateInterface> = async data => {
    try {
      if (null === data) {
        return;
      }
      if (undefined === sortingCenter || null === sortingCenter) {
        return;
      }
      const sortingCenterId = sortingCenter['@id'];
      const clientId = '/api/clients/' + client;

      let clientContracts: ClientContractCreation[] = data.materials.map(material => {
        return {
          buyer: data.buyer,
          material: material,
          sortingCenter: sortingCenterId,
          client: clientId,
          isActive: true,
        };
      });
      setIsSubmitting(true);
      clientRest.post('/client_contracts_batch', {
        clientContracts,
        sortingCenter: sortingCenterId,
      });

      PubSub.publish(Event.REFRESH_CLIENT_CONTRACTS);
      notify({
        message: pt('form.creation.success'),
        severity: 'success',
      });
      reset();

      history.push(backRoute);
    } catch (e) {
      notify({
        message: pt('form.creation.error'),
        severity: 'error',
      });
    }
    if (undefined !== setEditing) {
      setEditing(false);
    }
    setIsSubmitting(false);
  };
  return (
    <>
      <PageContent title={t('clientContracts.page.title')} backRoute={backRoute} withButtonBar={true}>
        <form id="client_contracts_form" onSubmit={handleSubmit(onSubmit)} className={classes.form}>
          <div className={classes.fieldLine}>
            <div className={classes.sharedLine}>
              <Controller
                name="buyer"
                control={control}
                render={({ field: { ref, onChange, ...fieldProps }, fieldState }) => (
                  <SelectWithErrorField
                    id="buyer"
                    choices={buyers}
                    choiceDisplayKey="name"
                    selectLabel={pt('form.buyer')}
                    error={fieldState.invalid}
                    errorMessage={fieldState.error?.message}
                    inputRef={ref}
                    InputLabelProps={{ required: true }}
                    onChange={e => {
                      onChange(e);
                      setValue('materials', []);
                    }}
                    {...fieldProps}
                  />
                )}
              />
            </div>
          </div>
          <div className={classes.fieldLine}>
            <div className={classes.materialLine}>
              <Controller
                name="materials"
                control={control}
                render={({ field: { onChange, value }, fieldState }) => (
                  <MaterialPicker
                    onValueChange={onChange}
                    label={pt('form.acceptedMaterials')}
                    placeholder={pt('form.materialPlaceholder')}
                    errorMessage={fieldState.error?.message}
                    error={Boolean(fieldState.error?.message)}
                    includeReject
                    required
                    requiredLabel="* :"
                    disabled={!materialsAllowed}
                    materialsAllowed={materialsAllowed}
                    value={value}
                  />
                )}
              />
            </div>
          </div>
        </form>
        <RequiredFieldsLegend />
      </PageContent>
      <BottomButtonBar
        disableNext={isSubmitting}
        previousButtonLink={backRoute}
        formName="client_contracts_form"
        previousStepLabel={t('productions.create.previousStepButtonLabel')}
        nextStepLabel={t('productions.create.createButtonLabel')}
        onClickNext={() => {
          handleSubmit(onSubmit);
        }}
      />
    </>
  );
};

export default ClientContractsForm;
